import _extends from "@babel/runtime/helpers/extends";
import { useFocusRing } from "@react-aria/focus";
import { useRadioGroup } from "@react-aria/radio";
import { useRadioGroupState } from "@react-stately/radio";
import { deepmerge } from "deepmerge-ts";
import React, { forwardRef, useMemo } from "react";
import { ThemeProvider, useTheme } from "styled-components";
import { Text } from "../Typography";
import { Label } from "../Typography/Label";
import { GroupContainer } from "./Elements/GroupContainer";
import { RootContainer } from "./Elements/RootContainer";
import { ToggleGroupContext } from "./ToggleGroupContext";
export const ToggleGroupRoot = /*#__PURE__*/forwardRef(renderToggleGroupRoot);
let instances = 0;
function renderToggleGroupRoot(_ref, ref) {
  var _ref2, _toggleGroupTheme$def;
  let {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as,
    children,
    description,
    descriptionAs,
    descriptionProps: descriptionPropsProp,
    disabled = false,
    error,
    errorAs,
    errorProps,
    id,
    groupContainerAs,
    groupContainerProps,
    label,
    labelAs,
    labelProps,
    name: nameProp,
    onChange,
    theme: customTheme,
    value,
    variant: variantProp,
    size = "m",
    ...containerProps
  } = _ref;
  const name = useMemo(() => nameProp !== null && nameProp !== void 0 ? nameProp : `toggle-group-${++instances}`, [nameProp]);
  const hookProps = {
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    description,
    errorMessage: error,
    isDisabled: disabled,
    id,
    label,
    name,
    onChange,
    orientation: "horizontal",
    validationState: error ? "invalid" : "valid",
    value
  };
  const {
    focusProps,
    isFocusVisible
  } = useFocusRing({
    within: true
  });
  const state = useRadioGroupState(hookProps);
  const {
    descriptionProps,
    errorMessageProps,
    labelProps: labelRadioProps,
    radioGroupProps
  } = useRadioGroup(hookProps, state);
  const theme = useTheme();
  const {
    components: {
      toggleGroup: toggleGroupTheme
    }
  } = theme;
  const variant = (_ref2 = variantProp !== null && variantProp !== void 0 ? variantProp : (_toggleGroupTheme$def = toggleGroupTheme.defaultProps) === null || _toggleGroupTheme$def === void 0 ? void 0 : _toggleGroupTheme$def.variant) !== null && _ref2 !== void 0 ? _ref2 : "secondary";
  const currentStyle = useMemo(() => {
    var _toggleGroupTheme$def2, _toggleGroupTheme$var, _toggleGroupTheme$var2;
    return deepmerge((_toggleGroupTheme$def2 = toggleGroupTheme.defaultStyle) !== null && _toggleGroupTheme$def2 !== void 0 ? _toggleGroupTheme$def2 : {}, (_toggleGroupTheme$var = (_toggleGroupTheme$var2 = toggleGroupTheme.variant) === null || _toggleGroupTheme$var2 === void 0 ? void 0 : _toggleGroupTheme$var2[variant]) !== null && _toggleGroupTheme$var !== void 0 ? _toggleGroupTheme$var : {}, customTheme !== null && customTheme !== void 0 ? customTheme : {});
  }, [customTheme && JSON.stringify(customTheme), toggleGroupTheme, variant]);
  const currentTheme = useMemo(() => deepmerge(theme, {
    components: {
      toggleGroup: {
        current: currentStyle
      }
    }
  }), [currentStyle, theme]);
  const contextValue = useMemo(() => ({
    state,
    size
  }), [state, size]);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: currentTheme
  }, /*#__PURE__*/React.createElement(RootContainer, _extends({
    as: as
  }, radioGroupProps, containerProps, {
    ref: ref
  }), label && /*#__PURE__*/React.createElement(Label, _extends({
    $minHeight: "3sp",
    variant: hookProps.isInvalid ? "danger" : undefined
  }, labelRadioProps, labelProps, {
    as: labelAs
  }), label), /*#__PURE__*/React.createElement(GroupContainer, _extends({
    isFocusVisible: isFocusVisible,
    isInvalid: hookProps.isInvalid
  }, focusProps, groupContainerProps, {
    as: groupContainerAs,
    size: size
  }), /*#__PURE__*/React.createElement(ToggleGroupContext.Provider, {
    value: contextValue
  }, children)), description && /*#__PURE__*/React.createElement(Text, _extends({
    $mt: "0.5sp",
    size: "s"
  }, descriptionProps, descriptionPropsProp, {
    as: descriptionAs !== null && descriptionAs !== void 0 ? descriptionAs : "div"
  }), description), error && /*#__PURE__*/React.createElement(Text, _extends({
    $mt: "0.5sp",
    size: "s",
    variant: "danger"
  }, errorMessageProps, errorProps, {
    as: errorAs !== null && errorAs !== void 0 ? errorAs : "div"
  }), error)));
}
//# sourceMappingURL=ToggleGroupRoot.js.map